import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import makeComponentTrashable from "trashable-react";
import {
    useStatusMessage,
    useModal,
    useTranslation,
} from "Shared/util/custom-hooks";
import {
    createUser,
    updateUser,
    removeUserFromUg,
    fetchUserBySsn,
} from "Shared/util/web-api";
import useUserContext from "Contexts/user-context";
import useAppContext from "Contexts/app-context";
import useTranslationContext from "Contexts/translation-context";
import { joinClassNames as c } from "Shared/util/styles";
import InlineSvg from "Shared/components/inline-svg";
import { Select } from "Shared/components/select";
import InputText from "Shared/components/input-text";
import Button from "Shared/components/button";
import TextLink from "Shared/components/text-link";
import StatusMessage from "Shared/components/status-message";
import ConfirmationModal from "Shared/components/modals/confirmation-modal";
import {
    UserRole,
    UserEditStatus,
    getUserRole,
} from "../../user-administration-utils";
import TabLink from "../tab-link";
import tabStyles from "./tab.css";
import styles from "./user-edit-main-tab.css";

const UserEditMainTab = ({
    display,
    registerPromise,
    status,
    onHide,
    selectedUser,
    organisation,
    tabOptions,
    displayTab,
}) => {
    const {
        appData: { eIdProvider },
    } = useAppContext();
    const { user, loading } = useUserContext();
    const { translationData } = useTranslationContext();
    const {
        user: { signInMethod },
        loaded,
        updateUserSessionToken,
    } = useUserContext();

    const [statusMessage, dispatchStatusMessage] = useStatusMessage();
    const { isModalOpen, openModal, closeModal } = useModal();

    const t = useTranslation((x) => x.userEdit);
    const tg = useTranslation((x) => x.global);
    const te = useTranslation((x) => x.eId);

    const [ssnUser, setSsnUser] = useState();

    const [socialSecurityNumber, setSocialSecurityNumber] = useState(undefined);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState({
        current: UserRole.UNKNOWN,
        new: UserRole.USER,
    });

    const [validFirstName, setValidFirstName] = useState(true);
    const [validLastName, setValidLastName] = useState(true);
    const [validPhone, setValidPhone] = useState(true);
    const [validEmail, setValidEmail] = useState(true);
    const [validSocialSecurityNumber, setValidSocialSecurityNumber] =
        useState(true);

    const [render, setRender] = useState("");

    const [hasMultipleOrganisations, setHasMultipleOrganisations] =
        useState(false);

    const [defaultOrganisation, setDefaultOrganisation] = useState({});

    const {
        id: selectedUserId,
        socialSecurityNumber: selectedUserSocialSecurityNumber,
        firstName: selectedUserFirstName,
        lastName: selectedUserLastName,
        phone: selectedUserPhone,
    } = selectedUser || {};

    const selectedUserEmail = selectedUser?.companies?.find(
        (x) => x.id === organisation.id,
    )?.userEmail;

    useEffect(() => {
        if (loading) {
            return;
        }

        if (display) {
            setDefaultOrganisation(user.defaultCompany);
            setHasMultipleOrganisations(user.companies.length > 1);
        }
    }, [display, user, loading]);

    useEffect(() => {
        const fillFields = () => {
            if (
                status === UserEditStatus.EDIT_OTHER ||
                status === UserEditStatus.EDIT_SELF
            ) {
                const userRole = getUserRole(selectedUser, organisation.id);

                setRole({ current: userRole, new: userRole });
                setSocialSecurityNumber(selectedUserSocialSecurityNumber);
                setFirstName(selectedUserFirstName);
                setLastName(selectedUserLastName);
                setPhone(selectedUserPhone);
                setEmail(selectedUserEmail);
            }
        };

        const resetFields = () => {
            setFirstName("");
            setLastName("");
            setPhone("");
            setEmail("");
            setRole({ current: UserRole.UNKNOWN, new: UserRole.USER });
            setRender(status);
            dispatchStatusMessage({ type: "NONE" });
        };

        if (status !== UserEditStatus.NONE) {
            fillFields();
            setRender(status);
        } else {
            resetFields();
        }
    }, [status, selectedUser, organisation]);

    const handleCreateUser = (e) => {
        e.preventDefault();

        const company = {
            id: organisation.id,
            role:
                role.new === UserRole.ADMIN
                    ? {
                          name: "Admin",
                          activeInCompany: true,
                      }
                    : {
                          name: "User",
                          activeInCompany: true,
                      },
            userEmail: email.toLowerCase(),
        };

        const newUser = {
            firstName,
            lastName,
            phone,
            disabled: true,
            companies: [company],
            socialSecurityNumber,
        };

        registerPromise(createUser(newUser, translationData.languageCode))
            .then(() => onHide("update"))
            .catch(() =>
                dispatchStatusMessage({
                    type: "FAILED",
                    message: t.userCouldNotBeCreated,
                }),
            );
    };

    const handleUpdateUser = (e) => {
        e.preventDefault();

        const userToUpdate = JSON.parse(JSON.stringify(selectedUser));
        userToUpdate.firstName = firstName;
        userToUpdate.lastName = lastName;
        userToUpdate.phone = phone;
        userToUpdate.companies.map((co) => {
            if (co.id === organisation.id) {
                co.role.name = role.new === UserRole.ADMIN ? "Admin" : "User";
            }
            return null;
        });
        userToUpdate.socialSecurityNumber = socialSecurityNumber;

        registerPromise(updateUser(userToUpdate))
            .then((response) => {
                if (userToUpdate.id === user.id) {
                    updateUserSessionToken(response);
                }
                onHide("update");
            })
            .catch((err) => {
                handleCatchRepsonse(err, t.userCouldNotBeUpdated);
            });
    };

    const handleRoleChanged = (newRole) =>
        setRole((prev) => ({ ...prev, new: newRole }));

    const handleRemoveUser = () => {
        registerPromise(removeUserFromUg(selectedUserId))
            .then(() => onHide("update"))
            .catch((err) => {
                handleCatchRepsonse(err, t.userCouldNotBeDeleted)
            });
    };

    const handleCatchRepsonse = (err, message) => {
        if (err.data.message === "atleastOneAdmin") {
            dispatchStatusMessage({
                type: "FAILED",
                message: t.atlestOneAdmin,
            });
        } else {
            dispatchStatusMessage({
                type: "FAILED",
                message: message,
            });
        }
    }

    const isCustomerSupport =
        loaded && user?.activeCompany.role.isCustomerSupport;

    const handleClose = () => onHide();

    const handleSubmit = (e) =>
        render === UserEditStatus.NEW
            ? handleCreateUser(e)
            : handleUpdateUser(e);

    const headerText =
        render === UserEditStatus.NEW
            ? t.addNewUser
            : `${selectedUserFirstName} ${selectedUserLastName}`;

    const roleItems = [
        { label: t.administrator, value: UserRole.ADMIN },
        { label: tg.user, value: UserRole.USER },
    ];

    const eIdFormatArgs = te[eIdProvider];
    const isSubmitEnabled =
        validFirstName &&
        validLastName &&
        validPhone &&
        validEmail &&
        validSocialSecurityNumber;

    useEffect(() => {
        if (validSocialSecurityNumber && socialSecurityNumber != null && socialSecurityNumber !== "") {
            registerPromise(fetchUserBySsn(socialSecurityNumber)).then(
                (response) => {
                    setSsnUser(response);
                },
            );
        }
    }, [validSocialSecurityNumber]);

    const handleAddUserToForm = () => {
        setFirstName(ssnUser?.firstName);
        setLastName(ssnUser?.lastName);
        setPhone(ssnUser?.phone);
        setEmail(ssnUser?.email);
    };

    const readOnly = !isCustomerSupport && render === UserEditStatus.EDIT_OTHER;

    return (
        <div
            className={c(
                tabStyles.container,
                display ? tabStyles.visible : tabStyles.hidden,
            )}
        >
            {render !== UserEditStatus.NONE && (
                <>
                    <StatusMessage
                        status={statusMessage.type}
                        text={statusMessage.message}
                        clearStatus={() =>
                            dispatchStatusMessage({ type: "NONE" })
                        }
                        className={styles.statusMessageContainer}
                    />
                    <div
                        className={c(
                            styles.headerContainer,
                            render === UserEditStatus.NEW && styles.alignLeft,
                        )}
                    >
                        <h3>{headerText}</h3>
                        {render !== UserEditStatus.NEW && (
                            <h5>{selectedUserEmail}</h5>
                        )}
                    </div>
                    <StatusMessage
                        status={
                            ssnUser && socialSecurityNumber
                                ? "WARNING"
                                : "NONE"
                        }
                        text={t.format(t.userWithSsnExist, {
                            userFullName: ssnUser?.fullName || "",
                            userSocialSecurityNumber: ssnUser?.socialSecurityNumber
                                ? ssnUser.socialSecurityNumber.slice(0, 8) +
                                "-" +
                                ssnUser.socialSecurityNumber.slice(8)
                                : "",
                            userEmail: ssnUser?.email || " ",
                            companies: ssnUser?.companies?.length > 0
                                ? ssnUser.companies.map((company) => company.ugName).join(", ")
                                : " "
                        })}
                        hideButton
                        className={styles.userAlreadyExistContainer}
                        actionButton={{
                            text: t.selectUser,
                            action: handleAddUserToForm,
                        }}
                    />
                    <StatusMessage
                        status={
                            socialSecurityNumber && !ssnUser && isCustomerSupport
                                ? "INFO"
                                : "NONE"
                        }
                        text={t.createUserWithSSN}
                        hideButton
                        className={styles.infoStatusMessageContainer}
                    />
                    <form className={tabStyles.content} onSubmit={handleSubmit}>
                        <div className={styles.textInputsContainer}>
                            {render !== UserEditStatus.EDIT_SELF && (
                                <Select
                                    label={t.role}
                                    items={roleItems}
                                    selectedValue={role.new}
                                    onChange={handleRoleChanged}
                                    disabled={role.current === UserRole.ADMIN && !isCustomerSupport}
                                    className={styles.selectRoleContainer}
                                />
                            )}
                            {isCustomerSupport && (
                                    <InputText
                                        inputType="socialSecurityNumber"
                                        value={socialSecurityNumber}
                                        onChange={(e) => {
                                            setSocialSecurityNumber(
                                                e.target.value,
                                            );
                                            setSsnUser(undefined);
                                        }}
                                        setEnabledSubmit={(bool) => {
                                            setValidSocialSecurityNumber(bool);
                                        }}
                                    />
                            )}
                            <InputText
                                inputType="firstname"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                setEnabledSubmit={(bool) =>
                                    setValidFirstName(bool)
                                }
                                isReadOnly={readOnly}
                                isRequired
                            />
                            <InputText
                                inputType="lastname"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                setEnabledSubmit={(bool) =>
                                    setValidLastName(bool)
                                }
                                isReadOnly={readOnly}
                                isRequired
                            />
                            <InputText
                                inputType="phone"
                                value={phone || ""}
                                onChange={(e) => setPhone(e.target.value)}
                                setEnabledSubmit={(bool) => setValidPhone(bool)}
                                isReadOnly={readOnly}
                            />
                            {render === UserEditStatus.NEW && (
                                <InputText
                                    inputType="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    setEnabledSubmit={(bool) =>
                                        setValidEmail(bool)
                                    }
                                    isRequired
                                />
                            )}
                        </div>
                        {signInMethod !== 1 && (
                            <TabLink
                                show={render === UserEditStatus.EDIT_SELF}
                                text={te.format(te.activate, eIdFormatArgs)}
                                action={() => displayTab(tabOptions.BANKID)}
                            />
                        )}
                        {signInMethod === 2 && (
                            <TabLink
                                show={render === UserEditStatus.EDIT_SELF}
                                text={t.changePassword}
                                action={() =>
                                    displayTab(tabOptions.CHANGE_PASS)
                                }
                            />
                        )}
                        {hasMultipleOrganisations && (
                            <TabLink
                                show={render === UserEditStatus.EDIT_SELF}
                                text={t.defaultOrganisation}
                                action={() =>
                                    displayTab(tabOptions.DEFAULT_ORG)
                                }
                                content={
                                    render === UserEditStatus.EDIT_SELF && (
                                        <div
                                            className={
                                                styles.defaultOrganisationInfoContainer
                                            }
                                        >
                                            <span>
                                                {defaultOrganisation.name}
                                            </span>
                                            <span>
                                                {
                                                    defaultOrganisation.organisationNumber
                                                }
                                            </span>
                                        </div>
                                    )
                                }
                            />
                        )}
                        <TabLink
                            show={render === UserEditStatus.EDIT_SELF}
                            text={t.addExistingBusiness}
                            action={() => displayTab(tabOptions.ADD_ORG)}
                        />
                        {render === UserEditStatus.EDIT_OTHER &&
                            (role.current === UserRole.USER ||
                                isCustomerSupport) && (
                                <TextLink
                                    text={
                                        <>
                                            <InlineSvg src="/trash.svg" />
                                            {t.deleteUser}
                                        </>
                                    }
                                    link={openModal}
                                    className={styles.deleteUserContainer}
                                />
                            )}
                        <div className={tabStyles.buttonsContainer}>
                            <Button
                                buttonType="button-secondary"
                                text={t.close}
                                onClick={handleClose}
                            />
                            <Button
                                text={tg.saveAndClose}
                                attributes={{
                                    type: "submit",
                                    disabled:
                                        !isSubmitEnabled,
                                }}
                                disabled={
                                    !isSubmitEnabled
                                }
                                className={tabStyles.submitButton}
                            />
                        </div>
                    </form>

                    <ConfirmationModal
                        isModalOpen={isModalOpen}
                        closeModal={closeModal}
                        headerText={t.areYouSureYouWantToDelete}
                        contentText={`${selectedUserFirstName} ${selectedUserLastName} (${selectedUserEmail})`}
                        confirmText={t.remove}
                        onConfirm={handleRemoveUser}
                    />
                </>
            )}
        </div>
    );
};

UserEditMainTab.propTypes = {
    display: PropTypes.bool.isRequired,
    selectedUser: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        id: PropTypes.number,
        companies: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                id: PropTypes.number,
            }),
        ),
    }),
    organisation: PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.number,
        organisationNumber: PropTypes.string,
        userEmail: PropTypes.string,
    }),
    status: PropTypes.oneOf(Object.values(UserEditStatus)).isRequired,
    onHide: PropTypes.func.isRequired,
    tabOptions: PropTypes.shape({
        BANKID: PropTypes.string,
        CHANGE_PASS: PropTypes.string,
        DEFAULT_ORG: PropTypes.string,
        ADD_ORG: PropTypes.string,
    }),
    displayTab: PropTypes.func.isRequired,
    registerPromise: PropTypes.func.isRequired,
};

export default makeComponentTrashable(UserEditMainTab);
